import type {
  InvestorNetWorthChange,
  InvestorNetWorthChangesResponse,
  NetWorthChangePeriod,
} from '@/interfaces/netWorthChanges';
import { toApiDateParam } from '@/utils/date';
import { advisorInvestorsApi } from './advisorInvestorsApi';

/**
 * Page size for the comparison. The widget ranks client-side, so it needs
 * every changed client; pages this large keep that to one request for all but
 * the biggest books, where the rest are fetched page by page.
 */
export const NET_WORTH_CHANGES_FETCH_LIMIT = 10000;

/**
 * Reads the endpoint's list response. An advisor with no nightly balances for
 * either date gets an empty `ListResponse` with no `Records` at all, so both
 * fields fall back rather than trusting their presence.
 */
export function normalizeNetWorthChanges(
  response: Partial<InvestorNetWorthChangesResponse> | null | undefined,
): InvestorNetWorthChangesResponse {
  const records = response?.Records ?? [];
  return {
    Records: records,
    TotalLength: response?.TotalLength ?? records.length,
  };
}

export const advisorNetWorthChangesApi = advisorInvestorsApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Every assigned client whose net worth moved between the two dates. The
     * advisor comes from the session, so the response is scoped server-side
     * and there is no client or household parameter to widen it with.
     *
     * Pages until the rows reach `TotalLength`, so a book larger than one page
     * is ranked in full rather than on its first page. An empty page also
     * stops it, so a `TotalLength` that overstates the rows cannot loop.
     */
    getInvestorNetWorthChanges: builder.query<
      InvestorNetWorthChangesResponse,
      NetWorthChangePeriod
    >({
      queryFn: async ({ startDate, endDate }, _api, _extra, baseQuery) => {
        const records: InvestorNetWorthChange[] = [];
        let totalLength = 0;

        for (let page = 1; ; page += 1) {
          const result = await baseQuery({
            url: '/api/advisor/InvestorHistoricalComparison',
            method: 'GET',
            params: {
              startDate: toApiDateParam(startDate),
              endDate: toApiDateParam(endDate),
              page,
              limit: NET_WORTH_CHANGES_FETCH_LIMIT,
            },
          });
          if (result.error) return { error: result.error };

          const response = normalizeNetWorthChanges(
            result.data as Partial<InvestorNetWorthChangesResponse> | null,
          );
          records.push(...response.Records);
          totalLength = response.TotalLength;

          if (response.Records.length === 0) break;
          if (records.length >= totalLength) break;
        }

        return { data: { Records: records, TotalLength: totalLength } };
      },
    }),
  }),
});

export const { useGetInvestorNetWorthChangesQuery } = advisorNetWorthChangesApi;
